import { z } from 'zod';

// Generic per-member key/value preference store (owner leads filter fix +
// follow-ups). The member is always the calling session member — never in the
// path — so the routes are addressed by workspace + key only.

// The preference keys the app actually uses. The store itself accepts any valid
// key (see MemberPreferenceKeySchema); this map exists for discoverability and
// so callers share one literal instead of hard-coding the string.
export const MEMBER_PREFERENCE_KEYS = {
  leadFilter: 'lead-filter',
  leadColumns: 'lead-columns',
  contactColumns: 'contact-columns',
  dealColumns: 'deal-columns',
  propertyColumns: 'property-columns',
  dealOverviewColumns: 'deal-overview-columns',
  valuationColumns: 'valuation-columns',
  factReviewColumns: 'fact-review-columns',
  activityColumns: 'activity-columns',
  companyColumns: 'company-columns',
  memberColumns: 'member-columns',
  memberInvitationColumns: 'member-invitation-columns',
  organizationColumns: 'organization-columns',
  adminColumns: 'admin-columns',
  apiKeyColumns: 'api-key-columns',
  mcpKeyColumns: 'mcp-key-columns',
  documentTemplateColumns: 'document-template-columns',
  emailTemplateColumns: 'email-template-columns',
  leadPartnerColumns: 'lead-partner-columns',
  skillRunColumns: 'skill-run-columns',
  connectedClientColumns: 'connected-client-columns',
  prospectColumns: 'prospect-columns',
} as const;

// A preference key: lowercase kebab, bounded so the KV namespace can't be
// polluted with arbitrary strings.
export const MemberPreferenceKeySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

// The stored value is arbitrary JSON: each view owns and validates its own shape
// client-side. Kept permissive on purpose so a new preference needs zero backend
// change — only a new key + a frontend shape.
export const MemberPreferenceValueSchema = z.json();
export type MemberPreferenceValue = z.infer<typeof MemberPreferenceValueSchema>;

export const MemberPreferencePathParamsSchema = z.object({
  workspaceId: z.uuid(),
  key: MemberPreferenceKeySchema,
});

// `null` when the member has never saved this key — the client then falls back to
// its own defaults.
export const GetMemberPreferenceResponseSchema = z.object({
  value: MemberPreferenceValueSchema.nullable(),
});
export type GetMemberPreferenceResponse = z.infer<typeof GetMemberPreferenceResponseSchema>;

export const SaveMemberPreferenceBodySchema = z.object({ value: MemberPreferenceValueSchema });
export const SaveMemberPreferenceResponseSchema = z.object({ value: MemberPreferenceValueSchema });
export type SaveMemberPreferenceResponse = z.infer<typeof SaveMemberPreferenceResponseSchema>;
